import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { usePicklists, useResources, useCompanyContacts, useResourceRoles, useCreateTicket } from '@/api/hooks';
import { ROUTES, ticketPath } from '@/lib/routes';
import { useClientScope } from '@/contexts/ClientScopeContext';

const SUPPORT_QUEUE_ID = 29682833;
const STATUS_NEW = 1;

function FormRow({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
      <label className="pt-2 text-sm font-medium text-muted-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      <div>{children}</div>
    </div>
  );
}

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export default function CreateTicketPage() {
  const navigate = useNavigate();
  const picklists = usePicklists();
  const resources = useResources();
  const createTicket = useCreateTicket();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Pre-filled from the client picked in the header (still changeable).
  const { scope } = useClientScope();
  const [company, setCompany] = useState<CompanyOption | null>(scope ? { id: scope.companyId, name: scope.name } : null);
  const [contactID, setContactID] = useState('');
  const [priority, setPriority] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [issueType, setIssueType] = useState('');
  const [subIssueType, setSubIssueType] = useState('');
  const [assignedResourceID, setAssignedResourceID] = useState('');
  const [assignedResourceRoleID, setAssignedResourceRoleID] = useState('');
  const assigneeRoles = useResourceRoles(assignedResourceID ? Number(assignedResourceID) : null);

  const contacts = useCompanyContacts(company?.id ?? null);

  // Reset contact when company changes
  useEffect(() => { setContactID(''); }, [company?.id]);

  // Default Priority → "Low" and Ticket Type → "Service Request" once picklists load.
  // Runs once so the user's own selection is never overwritten.
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (defaultsApplied.current || !picklists.data) return;
    defaultsApplied.current = true;
    const lowPriority = picklists.data.priority.find((o) => o.label.toLowerCase() === 'low');
    if (lowPriority) setPriority(String(lowPriority.value));
    const serviceRequest = picklists.data.ticketType.find((o) => o.label.toLowerCase() === 'service request');
    if (serviceRequest) setTicketType(String(serviceRequest.value));
  }, [picklists.data]);

  const pl = picklists.data;
  const subIssueOpts = pl?.subIssueType.filter((o) => String(o.parentValue) === issueType) ?? [];

  // Find "Phone" source value from picklists
  const phoneSourceValue = pl?.source.find((o) => o.label.toLowerCase() === 'phone')?.value;

  const canSubmit = title.trim() && company && priority;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const fields: Record<string, unknown> = {
      title: title.trim(),
      companyID: company!.id,
      status: STATUS_NEW,
      priority: Number(priority),
      queueID: SUPPORT_QUEUE_ID,
    };
    if (phoneSourceValue) fields.source = Number(phoneSourceValue);
    if (description.trim()) fields.description = description.trim();
    if (contactID) fields.contactID = Number(contactID);
    if (ticketType) fields.ticketType = Number(ticketType);
    if (issueType) fields.issueType = Number(issueType);
    if (subIssueType) fields.subIssueType = Number(subIssueType);
    if (assignedResourceID) {
      fields.assignedResourceID = Number(assignedResourceID);
      if (assignedResourceRoleID) fields.assignedResourceRoleID = Number(assignedResourceRoleID);
    }

    createTicket.mutate(fields, {
      onSuccess: (data) => {
        const id = (data as any)?.itemId ?? (data as any)?.item?.id ?? (data as any)?.id;
        toast.success('Ticket created');
        navigate(id ? ticketPath(id) : ROUTES.psa.tickets.mine);
      },
      onError: (err) => toast.error('Failed to create ticket', {
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
    });
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-4">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-[22px] font-[650] tracking-[-0.018em]">New Ticket</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
        <FormRow label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief description of the issue"
            required
          />
        </FormRow>

        <FormRow label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details…"
            rows={4}
          />
        </FormRow>

        <FormRow label="Company" required>
          <CompanySearch value={company} onChange={setCompany} />
        </FormRow>

        {company && (
          <FormRow label="Contact">
            <select
              value={contactID}
              onChange={(e) => setContactID(e.target.value)}
              className={SELECT_CLS}
              disabled={contacts.isLoading}
            >
              <option value="">{contacts.isLoading ? 'Loading…' : '— none —'}</option>
              {(contacts.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}{c.emailAddress ? ` — ${c.emailAddress}` : ''}
                </option>
              ))}
            </select>
          </FormRow>
        )}

        <FormRow label="Priority" required>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            required
            className={SELECT_CLS}
          >
            <option value="">Select priority…</option>
            {(pl?.priority ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormRow>

        <div className="border-t border-border pt-4">
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Optional</p>
          <div className="space-y-4">
            <FormRow label="Ticket Type">
              <select
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
                className={SELECT_CLS}
              >
                <option value="">— none —</option>
                {(pl?.ticketType ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </FormRow>

            <FormRow label="Issue Type">
              <select
                value={issueType}
                onChange={(e) => { setIssueType(e.target.value); setSubIssueType(''); }}
                className={SELECT_CLS}
              >
                <option value="">— none —</option>
                {(pl?.issueType ?? []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </FormRow>

            {issueType && subIssueOpts.length > 0 && (
              <FormRow label="Sub-Issue Type">
                <select
                  value={subIssueType}
                  onChange={(e) => setSubIssueType(e.target.value)}
                  className={SELECT_CLS}
                >
                  <option value="">— none —</option>
                  {subIssueOpts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FormRow>
            )}

            <FormRow label="Assignee">
              <select
                value={assignedResourceID}
                onChange={(e) => { setAssignedResourceID(e.target.value); setAssignedResourceRoleID(''); }}
                className={SELECT_CLS}
              >
                <option value="">Unassigned</option>
                {(resources.data?.items ?? []).map((r) => (
                  <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
                ))}
              </select>
            </FormRow>

            {assignedResourceID && (
              <FormRow label="Assignee Role">
                <select
                  value={assignedResourceRoleID}
                  onChange={(e) => setAssignedResourceRoleID(e.target.value)}
                  className={SELECT_CLS}
                  disabled={assigneeRoles.isLoading}
                >
                  <option value="">{assigneeRoles.isLoading ? 'Loading roles…' : '— select role —'}</option>
                  {(assigneeRoles.data?.items ?? []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </FormRow>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={!canSubmit || createTicket.isPending}>
            {createTicket.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Ticket
          </Button>
        </div>
      </form>
    </div>
  );
}
